// Parsing of stored article payloads (article_draft / article_body / article_source_raw)
// into EditorJS blocks for the article editor page. Extracted verbatim from Article.js;
// behavior is pinned by articleContent.test.js.

/**
 * Markdown bold -> <b>, and source markers 【n:m†info】 / [n:m] -> clickable <sup>.
 */
export const applyBoldFormatting = (text) => {
  // First handle double asterisks **text** (standard markdown bold)
  const doubleBoldRegex = /\*\*(.*?)\*\*/g;
  // Then handle single asterisks *text* (alternative bold format)
  // Use negative lookbehind/lookahead to avoid matching asterisks that are part of **text**
  const singleBoldRegex = /(?<!\*)\*([^*]+?)\*(?!\*)/g;
  const sourceRegex = /【(\d+):(\d+)†(.+?)】/g;
  const squareBracketSourceRegex = /\[(\d+):(\d+)\]/g;

  // First replace double asterisks
  let formattedText = text.replace(doubleBoldRegex, '<b>$1</b>');
  // Then replace single asterisks (that weren't already part of **)
  formattedText = formattedText.replace(singleBoldRegex, '<b>$1</b>');

  formattedText = formattedText.replace(sourceRegex, (match, sourceNum, itemNum, fullInfo) => {
    return `<sup class="source-reference cursor-pointer font-bold text-blue-600 hover:underline" data-source="${sourceNum}" data-item="${itemNum}" data-full-info="${fullInfo}">[${sourceNum}:${itemNum}]</sup>`;
  });

  formattedText = formattedText.replace(squareBracketSourceRegex, (match, sourceNum, itemNum) => {
    return `<sup class="source-reference cursor-pointer font-bold text-blue-600 hover:underline" data-source="${sourceNum}" data-item="${itemNum}" data-full-info="Source ${sourceNum}">[${sourceNum}:${itemNum}]</sup>`;
  });

  return formattedText;
};

// Extract featured image from HTML content if present
export const extractImageFromHtml = (contentStr) => {
  // First, look for Markdown-style images
  const mdImageRegex = /!\[(.*?)\]\((.*?)\)/;
  const mdMatch = contentStr.match(mdImageRegex);

  if (mdMatch) {
    return {
      type: 'image',
      data: {
        file: {
          url: mdMatch[2]
        },
        caption: mdMatch[1],
        withBorder: false,
        withBackground: false,
        stretched: false
      }
    };
  }

  // Then look for HTML images
  const imgRegex = /<img[^>]+src="([^"]+)"[^>]+alt="([^"]+)"[^>]*>/;
  const imgMatch = contentStr.match(imgRegex);

  if (imgMatch) {
    const imgSrc = imgMatch[1];
    const imgAlt = imgMatch[2];

    // Get attribution info if available
    let caption = imgAlt;
    const attributionRegex = /<p class="image-attribution">Photo by <a[^>]+href="([^"]+)"[^>]*>([^<]+)<\/a> on/;
    const attributionMatch = contentStr.match(attributionRegex);

    if (attributionMatch) {
      const photographerName = attributionMatch[2];
      caption = `Photo by ${photographerName} on Pexels`;
    }

    return {
      type: 'image',
      data: {
        file: {
          url: imgSrc
        },
        caption,
        withBorder: false,
        withBackground: false,
        stretched: false
      }
    };
  }

  return null;
};

export const stripReferencesSectionFromBlocks = (blocks) => {
  if (!Array.isArray(blocks)) return blocks;
  const normalize = (value) => String(value || '').trim().toLowerCase();
  const refIndex = blocks.findIndex(
    (block) => block?.type === 'header'
      && Number(block?.data?.level) === 2
      && ['references', 'bibliografia', 'bibliography', 'źródła'].includes(normalize(block?.data?.text))
  );
  if (refIndex < 0) return blocks;
  return blocks.slice(0, refIndex);
};

export const parseArticleSourceRaw = (rawValue) => {
  if (!rawValue) return null;
  try {
    const parsed = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
    if (!parsed || typeof parsed !== 'object') return null;
    const usedSources = Array.isArray(parsed.used_sources) ? parsed.used_sources : [];
    const availableSources = Array.isArray(parsed.available_sources) ? parsed.available_sources : [];
    const schemaVersion = Number.isFinite(Number(parsed.schema_version))
      ? Number(parsed.schema_version)
      : 1;

    return {
      ...parsed,
      schema_version: schemaVersion,
      used_sources: usedSources,
      available_sources: availableSources,
      citation_resolution_strategy: parsed.citation_resolution_strategy || null,
      generated_at: parsed.generated_at || null,
      openai_annotations_raw_count: Number(parsed.openai_annotations_raw_count || 0),
      file_search_results_count: Number(parsed.file_search_results_count || 0),
      inline_citation_marker_count: Number(parsed.inline_citation_marker_count || 0)
    };
  } catch (error) {
    console.error('Error parsing article_source_raw:', error);
    return null;
  }
};

/**
 * Normalizes any stored article content shape (EditorJS JSON, social sections,
 * markdown `content` payload, plain text) into { blocks }.
 */
export const parseArticleContent = (content) => {
  if (!content) {
    return { blocks: [] };
  }

  try {
    let parsedContent;
    if (typeof content === 'string') {
      parsedContent = JSON.parse(content);
    } else {
      parsedContent = content;
    }

    // Handle social media post format
    if (parsedContent && parsedContent.sections) {
      const blocks = [];
      parsedContent.sections.forEach(section => {
        if (section.header) {
          blocks.push({
            type: 'header',
            data: { text: applyBoldFormatting(section.header), level: 3 }
          });
        }
        if (section.paragraph) {
          blocks.push({
            type: 'paragraph',
            data: { text: applyBoldFormatting(section.paragraph) }
          });
        }
      });
      return { blocks };
    }

    if (parsedContent && typeof parsedContent.content === 'string') {
      const contentStr = parsedContent.content;
      const blocks = [];

      // Check for featured image at the beginning
      const imageBlock = extractImageFromHtml(contentStr);
      if (imageBlock) {
        blocks.push(imageBlock);

        // Remove the image HTML from the content to avoid duplicates
        parsedContent.content = parsedContent.content
          .replace(/<style>[\s\S]*?<\/style>/, '')
          .replace(/<div class="featured-image-container">[\s\S]*?<\/div>/, '')
          .replace(/!\[.*?\]\(.*?\)/, '');
      }

      const lines = parsedContent.content.split('\n');
      let currentTable = null;

      lines.forEach(line => {
        line = line.trim();

        // Skip empty lines
        if (!line) {
          currentTable = null;
          return;
        }

        // Check for Markdown image syntax
        const mdImageMatch = line.match(/!\[(.*?)\]\((.*?)\)/);
        if (mdImageMatch) {
          blocks.push({
            type: 'image',
            data: {
              file: {
                url: mdImageMatch[2]
              },
              caption: mdImageMatch[1],
              withBorder: false,
              withBackground: false,
              stretched: false
            }
          });
          return;
        }

        // Upewniamy się, że regex poprawnie wykrywa nawiasy 【】
        const sourceMatch = line.match(/【(.+?)】/);
        if (sourceMatch) {
          // Sprawdź, czy zawiera format "cyfry:cyfry-cyfry†nazwa_pliku.txt"
          const reference = sourceMatch[1];
          if (reference.match(/\d+:\d+-\d+†.+/)) {
            blocks.push({ 
              type: 'drawer', 
              data: { 
                text: line,
                fileReference: reference
              } 
            });
          } else {
            // Jeśli to nie jest właściwy format, traktuj jak normalny tekst
            blocks.push({
              type: 'paragraph',
              data: {
                text: applyBoldFormatting(line)
              }
            });
          }
        } else if (line.startsWith('# ')) {
          blocks.push({ type: 'header', data: { text: applyBoldFormatting(line.substring(2)), level: 1 } });
        } else if (line.startsWith('## ')) {
          blocks.push({ type: 'header', data: { text: applyBoldFormatting(line.substring(3)), level: 2 } });
        } else if (line.startsWith('### ')) {
          blocks.push({ type: 'header', data: { text: applyBoldFormatting(line.substring(4)), level: 3 } });
        } else if (line.startsWith('#### ')) {
          blocks.push({ type: 'header', data: { text: applyBoldFormatting(line.substring(5)), level: 4 } });
        } else if (line.startsWith('- ')) {
          if (blocks.length > 0 && blocks[blocks.length - 1].type === 'list') {
            blocks[blocks.length - 1].data.items.push(applyBoldFormatting(line.substring(2)));
          } else {
            blocks.push({ type: 'list', data: { style: 'unordered', items: [applyBoldFormatting(line.substring(2))] } });
          }
        } else if (line.startsWith('|')) {
          if (!currentTable) {
            currentTable = { type: 'table', data: { withHeadings: true, content: [] } };
            blocks.push(currentTable);
          }

          // Sprawdź, czy to linia separatora (zawierająca głównie "-")
          const isSeparatorRow = line.split('|')
            .filter(cell => cell.trim().length > 0)
            .every(cell => cell.trim().replace(/:-*:?|-+/g, '').length === 0);

          // Dodaj komórki do tabeli tylko jeśli to nie jest linia separatora
          if (!isSeparatorRow) {
            const cells = line.split('|').slice(1, -1).map(cell => applyBoldFormatting(cell.trim()));
            currentTable.data.content.push(cells);
          }
        } else if (line === '---') {
          blocks.push({ type: 'delimiter', data: {} });
        } else {
          blocks.push({
            type: 'paragraph',
            data: {
              text: applyBoldFormatting(line)
            }
          });
        }
      });

      return { blocks };
    }

    if (parsedContent && Array.isArray(parsedContent.blocks)) {
      // Process and fix any potential issues with the blocks
      const processedBlocks = parsedContent.blocks.map(block => {
        // Fix image blocks if needed
        if (block.type === 'image') {
          const imageUrl = block?.data?.file?.url || block?.data?.url || '';
          if (!imageUrl) {
            // Invalid image block, skip it
            return null;
          }

          return {
            ...block,
            data: {
              ...block.data,
              file: {
                ...(block.data?.file || {}),
                // Ensure image URLs use https for security
                url: imageUrl.replace('http://', 'https://')
              },
              url: undefined
            }
          };
        }

        if (block.type === 'drawer') {
          return block;
        } else if (block.type === 'list') {
          return {
            ...block,
            data: {
              ...block.data,
              items: block.data.items.map(item => applyBoldFormatting(item))
            }
          };
        } else if (block.type === 'table') {
          return {
            ...block,
            data: {
              ...block.data,
              content: block.data.content.map(row => row.map(cell => applyBoldFormatting(cell)))
            }
          };
        } else if (block.type === 'paragraph' || block.type === 'header') {
          return {
            ...block,
            data: {
              ...block.data,
              text: applyBoldFormatting(block.data.text)
            }
          };
        }
        return block;
      }).filter(block => block !== null);

      return { blocks: processedBlocks };
    }

    return {
      blocks: [
        {
          type: 'paragraph',
          data: {
            text: applyBoldFormatting(typeof parsedContent === 'string' ? parsedContent : JSON.stringify(parsedContent))
          }
        }
      ]
    };
  } catch (error) {
    console.error('Error parsing article content:', error);
    return {
      blocks: [
        {
          type: 'paragraph',
          data: {
            text: typeof content === 'string' ? content : JSON.stringify(content)
          }
        }
      ]
    };
  }
};

/**
 * Blocks shown in the editor for a fetched article: drafts show article_draft as-is,
 * finished articles show article_body without the trailing references section.
 */
export const getArticleDisplayBlocks = (data) => {
  const content = data.status === 'draft' ? data.article_draft : data.article_body;

  const { blocks } = parseArticleContent(content);
  return data.status === 'draft'
    ? blocks
    : stripReferencesSectionFromBlocks(blocks);
};
